import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
	Container,
	Grid,
	Card,
	Image,
	Text,
	Loader,
	Center,
	Title,
	Alert,
} from '@mantine/core';
import {
	fetchCategories,
	fetchCategoryProductsByName,
} from '../../services/api';
import { Product, Category } from '../../types'; // Use consistent shared types

interface CategoriesProps {
	isHomePage?: boolean; // Flag to differentiate home page and category details
}

const Categories: React.FC<CategoriesProps> = ({ isHomePage = false }) => {
	const { categoryName } = useParams<{ categoryName: string }>();
	const [products, setProducts] = useState<Product[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isHomePage) {
			fetchCategories()
				.then((data: Category[]) => {
					setCategories(data);
					setLoading(false);
				})
				.catch((err) => {
					console.error('Error fetching categories:', err);
					setError('Failed to load categories. Please try again later.');
					setLoading(false);
				});
		} else if (categoryName) {
			fetchCategoryProductsByName(categoryName)
				.then((data: Product[]) => {
					setProducts(data);
					setLoading(false);
				})
				.catch((err) => {
					console.error('Error fetching products:', err);
					setError('Failed to load products. Please try again later.');
					setLoading(false);
				});
		}
	}, [isHomePage, categoryName]);

	if (loading) {
		return (
			<Center style={{ minHeight: '50vh' }}>
				<Loader />
			</Center>
		);
	}

	if (error) {
		return (
			<Center style={{ minHeight: '50vh' }}>
				<Alert
					title="Error"
					color="red">
					{error}
				</Alert>
			</Center>
		);
	}

	return (
		<Container>
			{isHomePage ? (
				<><br/>
					<Title order={2}>Browse Our Products For Sale</Title><br/>
					<Grid>
						{categories.map((category) => (
							<Grid.Col
								span={12}
								key={category.categoryId}>
								<Card
									component={Link}
									to={`/categories/${category.categoryName}`}
									shadow="sm"
									padding="lg">
									<Text size="lg">{category.categoryName}</Text>
								</Card>
							</Grid.Col>
						))}
					</Grid>
				</>
			) : (
				<>
					<Title
						order={2}
						style={{ marginBottom: '1rem' }}>
						Products in {categoryName}
					</Title>
					<Grid>
						{products.map((product) => (
							<Grid.Col
								span={12}
								key={product.productId}>
								<Card
									shadow="sm"
									padding="lg">
									<Card.Section
										component={Link}
										to={`/products/${product.productId}`}>
										<Image
											src={`/uploads/${product.imagePaths[0]}`}
											height={200}
											fit="cover"
										/>
									</Card.Section>
									<Text
										size="lg"
										mt="sm">
										{product.productTitle}
									</Text>
									<Text
										size="sm"
										color="dimmed">
										£{product.price.toFixed(2)}
									</Text>
								</Card>
							</Grid.Col>
						))}
					</Grid>
				</>
			)}
		</Container>
	);
};

export default Categories;
